import InformationReliability from '@/components/content/informationreliability/InformationReliability';
import MalwareKitsDrawer from '../../catalogs/features/malwares/components/MalwareKitsDrawer';
import List from '@/components/content/list/List';
import ThreatActorsDrawer from '../../catalogs/features/threatactors/components/ThreatActorDrawer';
import VulnerabilitiesDrawer from '../../catalogs/features/vulnerabilities/components/VulnerabilitiesDrawer';
import RiskIndicators from '../../riskindicators/RiskIndicators';
import SourcesDrawer from '../../catalogs/features/sources/components/SourcesDrawer';
import SourceLinkButton from '../components/SourceLinkButton';

import { signalSourceUrl } from '../services/signals';
import { getEntries, getValues } from '@/utils';
import { formatIsoDate } from '@/utils/date';
import { createHelper } from '../../issue/utils/helper';

import { type Module, ModuleLayouts } from '../../issue/types/modulesv2';
import { CollectorType, SignalCollectors, SignalEntity } from '../types';

import AiIcon from '@/assets/icons/content/ai.svg';
import TranslatedIcon from '@/assets/icons/content/translated.svg';

import classes from '@/client/features/issue/components/IssueModule.module.css';
import { Group, Tooltip } from '@mantine/core';
import { PostContextHistory } from '@/client/features/signals/components/PostsContextHistory';
import { FeatureFlag } from '@/utils/featureFlags';
import { isFeatureEnabled } from '@/utils/featureEnabled';

const helper = createHelper<SignalEntity>();
const showSignalPostsContext = isFeatureEnabled(FeatureFlag.ShowSignalPostsContext);

export const sourceLeft = (collector: SignalCollectors): Array<Module<SignalEntity>> => [
  {
    title: 'General Information',
    submodules: [
      helper.accessor(
        (data) =>
          data.ai_summary || data.summary
            ? {
                ai: data.ai_summary,
                summary: data.summary,
              }
            : null,
        {
          element: ({ getValue }) => {
            const { ai, summary } = getValue();
            return (
              <>
                <h3 className={classes.submoduleTitle}>
                  <Group gap={5} component="span">
                    {ai ? 'AI' : ''} Summary
                    {ai && <AiIcon />}
                  </Group>
                </h3>

                {ai || summary}
              </>
            );
          },
        }
      ),
      helper.accessor(
        (data) =>
          data.text || data.translated_text
            ? {
                language: data.language,
                content: data.text,
                translated: data.translated_text,
              }
            : null,
        {
          scroll: false,
          element: ({ getValue }) => {
            const { language, content, translated } = getValue();
            const isEnglish = language === 'en';

            return (
              <>
                <h3 className={classes.submoduleTitle}>
                  <Group gap={5} component="span">
                    {!isEnglish && translated ? 'Translated' : ''} Content
                    {!isEnglish && (
                      <Tooltip label={language} disabled={!language}>
                        <TranslatedIcon />
                      </Tooltip>
                    )}
                  </Group>
                </h3>

                {!isEnglish && translated ? translated : content}
              </>
            );
          },
        }
      ),
    ],
  },
  {
    hide: collector !== 'forum-posts' && collector !== 'telegram',
    title: 'Context Information',
    submodules: [
      helper.accessor((data) => data.credential.domain, {
        title: 'Author',
      }),
      helper.accessor((data) => data.author.name, {
        title: 'Author Name',
      }),
      helper.accessor((data) => data.author.jobtitle, {
        title: 'Author Title',
      }),
      helper.accessor((data) => data.thread.title, {
        title: 'Thread Title',
      }),
      helper.accessor((data) => data, {
        hide:
          !showSignalPostsContext ||
          (collector !== CollectorType.ForumPosts && collector !== CollectorType.Telegram),
        title: (
          <Group gap={4} align="center">
            <span>Show context</span>
            <TranslatedIcon />
          </Group>
        ),
        element: ({ getValue }) => {
          const signalData = getValue();
          const collector = signalData.collector;
          const signalPublishedAt = signalData.published_at;

          const threadPath =
            collector === CollectorType.ForumPosts
              ? signalData.thread.path
              : (signalData.raw as any).data.structured.Channel.Id;
          const signalId = (signalData.raw as any).metadata.id;

          return (
            <PostContextHistory
              signalPublishedAt={signalPublishedAt}
              signalId={signalId}
              threadPath={threadPath}
              collector={collector}
            />
          );
        },
        scroll: false,
      }),
    ],
  },
  {
    hide: collector !== 'telegram',
    title: 'Context Information',
    direction: { base: 'column', s: 'row' },
    wrap: 'wrap',
    submodules: [
      helper.accessor((data) => data.author.telegram, {
        title: 'Author',
      }),
      helper.accessor((data) => data.channel.title, {
        title: 'Thread Title',
      }),
    ],
  },
];

export const sourceRight = (): Array<Module<SignalEntity>> => [
  {
    submodules: [
      helper.accessor(
        (data) => ({
          locations: getEntries(data.catalog.locations).map(([id, name]) => ({ id, name })),
          technologies: getEntries(data.catalog.technologies).map(([id, name]) => ({ id, name })),
          industry_sectors: getEntries(data.catalog.industry_sectors).map(([id, name]) => ({
            id,
            name,
          })),
        }),
        {
          title: 'Your Relevancy',
          element: RiskIndicators,
          scroll: false,
        }
      ),
    ],
  },
  {
    title: 'Signal Information',
    submodules: [
      helper.accessor((data) => data.id, {
        title: 'ID',
      }),
      helper.accessor((data) => data.catalog.category, {
        title: 'Category',
      }),
      helper.accessor((data) => formatIsoDate(data.ingested_at), {
        title: 'Ingested At',
      }),
      helper.accessor((data) => formatIsoDate(data.published_at), {
        title: 'Published At',
      }),
      helper.accessor((data) => formatIsoDate(data?.updated_at), {
        title: 'Updated At',
      }),
    ],
  },
  {
    title: 'Source Information',
    submodules: [
      helper.accessor(
        (data) => {
          return { id: data.sourceInformation?.id ?? '', name: data.sourceInformation?.name ?? '' };
        },
        {
          title: 'Source',
          element: ({ getValue }) => {
            const { id, name } = getValue();

            return <SourcesDrawer id={id} name={name || `${id}`} />;
          },
        }
      ),
      helper.accessor(
        (data) => ({
          type: data.sourceInformation?.type ?? '',
          type2: data.sourceInformation?.subtype ?? '',
        }),
        {
          title: 'Source Type',
          element: ({ getValue }) => {
            const { type, type2 } = getValue();

            return `${type} - ${type2}`;
          },
        }
      ),
      helper.accessor((data) => data.sourceInformation?.reliability, {
        title: 'Source Reliability Score',
        element: ({ getValue }) => <InformationReliability value={getValue()} />,
      }),
      helper.accessor((data) => signalSourceUrl(data.link), {
        title: 'Source Link',
        element: ({ getValue }) => <SourceLinkButton url={getValue()} />,
      }),
      helper.accessor((data) => formatIsoDate(data.sourceInformation?.updated_at), {
        title: 'Source DTG',
      }),
    ],
  },
  {
    title: 'Related Entities',
    layout: ModuleLayouts.GRID,
    submodules: [
      helper.accessor((data) => getEntries(data.catalog.threatactor), {
        title: 'Threat Actors',
        element: ({ getValue }) => (
          <List
            items={getValue()}
            renderItem={([id, name]) => {
              return <ThreatActorsDrawer id={+id} name={name} />;
            }}
          />
        ),
      }),
      helper.accessor(
        (data) => {
          return getEntries(data.catalog.vulnerability);
        },
        {
          title: 'Vulnerabilities',
          element: ({ getValue }) => (
            <List
              items={getValue()}
              renderItem={([id, name]) => {
                return <VulnerabilitiesDrawer id={+id} name={name} />;
              }}
            />
          ),
        }
      ),
      helper.accessor((data) => getEntries(data.catalog.malwarekit), {
        title: 'Malware Kits',
        element: ({ getValue }) => (
          <List
            items={getValue()}
            renderItem={([id, name]) => {
              return <MalwareKitsDrawer id={+id} name={name} />;
            }}
          />
        ),
      }),
      helper.accessor((data) => getValues(data.catalog.technologies), {
        title: 'Technologies',
      }),
    ],
  },
];
